import { Badge } from '@/components/ui/badge';
import { CardType, PlotCard } from '@/pages/timeline/types';
import { BookOpen, Plus, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { Dialog, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AddPlotCardDialogContent } from '@/pages/timeline/components/PlotCardDialog';
import { useUpdatePlotCard, useCreatePlotCard, useDeletePlotCard, useSecureTimeline, useChapterPlotCards } from '@/services/query/timeline-queries';
import { useChapter } from '@/hooks/useChapters';
import { useParams } from 'react-router-dom';
import { useToast } from '@/components/ui/use-toast';
import { cn } from '@/lib/utils';
import { PlotCardDetailsModal } from '@/pages/timeline/components/PlotCardDetailsModal';

interface ChapterPlotcardsTabProps {
  chapterId?: string | number;
}

import { SidebarHeader } from '@/components/ui/sidebar';

export function ChapterPlotcardsTab({ chapterId }: ChapterPlotcardsTabProps) {
  const { projectId } = useParams();
  const { toast } = useToast();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedCard, setSelectedCard] = useState<PlotCard | null>(null);
  
  // Fetch plotcards for this chapter
  const { data: plotCards = [], isLoading } = useChapterPlotCards(
    Number(chapterId)
  );

  // Fetch chapter details for context
  const { data: chapter } = useChapter(chapterId);

  // Fetch timelines for the creation dialog
  const { data: secureTimelineData } = useSecureTimeline(projectId || '');
  
  const createPlotCardMutation = useCreatePlotCard();
  const updatePlotCardMutation = useUpdatePlotCard();
  const deletePlotCardMutation = useDeletePlotCard();

  const handleOpenCreateDialog = () => {
    setSelectedCard(null);
    setIsDialogOpen(true);
  };

  const handleOpenEditDialog = (card: PlotCard) => {
    setSelectedCard(card);
    setIsEditModalOpen(true);
  };

  const plotLines = secureTimelineData?.plotLines?.map((pl: any) => ({
    id: pl.id,
    title: pl.title
  })) || [];

  const handleSavePlotCard = async (data: any) => {
    if (!secureTimelineData?.timeline?.id) return;

    try {
      // Create new card
      await createPlotCardMutation.mutateAsync({
        timelineId: secureTimelineData.timeline.id.toString(),
        plotLineId: data.plotLineId.toString(),
        title: data.title,
        content: data.content,
        cardType: data.type,
        chapterId: Number(chapterId)
      });
      
      toast({
        title: "Plot Point Created",
        description: "Your new plot point has been added.",
      });
      setIsDialogOpen(false);
    } catch (error) {
      toast({
        title: "Error",
        description: `Failed to create plot point.`,
        variant: "destructive"
      });
    }
  };

  const handleUpdatePlotCard = async (card: PlotCard, updates: Partial<PlotCard>) => {
    if (!secureTimelineData?.timeline?.id) return;

    try {
      await updatePlotCardMutation.mutateAsync({
        cardId: card.id.toString(),
        updates,
        timelineId: secureTimelineData.timeline.id.toString()
      });
      toast({
        title: "Plot Point Updated",
        description: "Changes have been saved.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update plot point.",
        variant: "destructive"
      });
    }
  };

  const handleDeletePlotCard = async (card: PlotCard) => {
    if (!secureTimelineData?.timeline?.id) return;

    try {
      await deletePlotCardMutation.mutateAsync({
        cardId: card.id.toString(),
        timelineId: secureTimelineData.timeline.id.toString()
      });
      toast({
        title: "Plot Point Deleted",
        description: "The plot point has been removed.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete plot point.",
        variant: "destructive"
      });
    }
  };

  const handleDuplicatePlotCard = async (card: PlotCard) => {
    if (!secureTimelineData?.timeline?.id) return;

    try {
      await createPlotCardMutation.mutateAsync({
        timelineId: secureTimelineData.timeline.id.toString(),
        plotLineId: card.x.toString(),
        title: `${card.title} (Copy)`,
        content: card.content,
        cardType: card.type,
        chapterId: card.chapter_id || Number(chapterId)
      });
      toast({
        title: "Plot Point Duplicated",
        description: "A copy has been created.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to duplicate plot point.",
        variant: "destructive"
      });
    }
  };

  // Get color styling for plotcard type - matching the timeline design
  const getPlotCardStyle = (type: string) => {
    const cardType = type.toLowerCase() as CardType;

    switch (cardType) {
      case 'plot':
        return {
          dot: 'bg-blue-500 dark:bg-blue-400',
          card: 'bg-blue-50 border-blue-200 dark:bg-blue-950/50 dark:border-blue-800',
          hover: 'hover:bg-blue-100 dark:hover:bg-blue-900/50'
        };
      case 'character':
        return {
          dot: 'bg-emerald-500 dark:bg-emerald-400',
          card: 'bg-emerald-50 border-emerald-200 dark:bg-emerald-950/50 dark:border-emerald-800',
          hover: 'hover:bg-emerald-100 dark:hover:bg-emerald-900/50'
        };
      case 'setting':
        return {
          dot: 'bg-violet-500 dark:bg-violet-400',
          card: 'bg-violet-50 border-violet-200 dark:bg-violet-950/50 dark:border-violet-800',
          hover: 'hover:bg-violet-100 dark:hover:bg-violet-900/50'
        };
      case 'conflict':
        return {
          dot: 'bg-rose-500 dark:bg-rose-400',
          card: 'bg-rose-50 border-rose-200 dark:bg-rose-950/50 dark:border-rose-800',
          hover: 'hover:bg-rose-100 dark:hover:bg-rose-900/50'
        };
      case 'theme':
        return {
          dot: 'bg-amber-500 dark:bg-amber-400',
          card: 'bg-amber-50 border-amber-200 dark:bg-amber-950/50 dark:border-amber-800',
          hover: 'hover:bg-amber-100 dark:hover:bg-amber-900/50'
        };
      case 'note':
        return {
          dot: 'bg-slate-500 dark:bg-slate-400',
          card: 'bg-slate-50 border-slate-200 dark:bg-slate-800 dark:border-slate-700',
          hover: 'hover:bg-slate-100 dark:hover:bg-slate-700/50'
        };
      case 'ai-context-prompt':
        return {
          dot: 'bg-teal-500 dark:bg-teal-400',
          card: 'bg-teal-50 border-teal-200 dark:bg-teal-950/50 dark:border-teal-800',
          hover: 'hover:bg-teal-100 dark:hover:bg-teal-900/50'
        };
      default:
        return {
          dot: 'bg-slate-500 dark:bg-slate-400',
          card: 'bg-slate-50 border-slate-200 dark:bg-slate-800 dark:border-slate-700',
          hover: 'hover:bg-slate-100 dark:hover:bg-slate-700/50'
        };
    }
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex-1 flex flex-col items-center justify-center p-6 min-h-0">
          <span className="animate-pulse text-xs text-muted-foreground">Loading plot elements...</span>
        </div>
      );
    }

    if (plotCards.length === 0) {
      return (
        <div className="flex-1 flex flex-col items-center justify-center p-8 text-center min-h-0">
          <div className="w-12 h-12 rounded-2xl bg-muted/50 flex items-center justify-center mb-4 ring-1 ring-border/50">
            <BookOpen className="h-6 w-6 text-muted-foreground/30" />
          </div>
          <p className="text-sm font-medium text-foreground mb-1">Quiet Chapter</p>
          <p className="text-xs text-muted-foreground max-w-[200px] opacity-60">
            No plot cards attached to this chapter yet.
          </p>
        </div>
      );
    }

    return (
      <div className="flex-1 w-full overflow-y-auto overflow-x-hidden min-h-0">
        <div className="p-4 space-y-3">
          {plotCards.map(card => (
            <PlotCardItem 
              key={card.id} 
              card={card} 
              getStyle={getPlotCardStyle} 
              onClick={() => handleOpenEditDialog(card)}
            />
          ))}
          {/* Spacer for bottom */}
          <div className="h-4" />
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full w-full bg-transparent">
      <SidebarHeader className="h-12 flex flex-row items-center justify-between border-b px-4 py-0 shrink-0">
        <div className="flex items-center gap-2 font-semibold text-sm">
          <BookOpen className="h-4 w-4" />
          <span>Plot Points</span>
        </div>
      </SidebarHeader>
      {renderContent()}

      {/* Fixed Footer for Adding Plot Point */}
      <div className="flex-shrink-0 z-10 border-t border-border/80 bg-background/80 backdrop-blur-xl">
        <div className="p-4 pt-4 pb-5">
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button
                variant="ghost"
                onClick={handleOpenCreateDialog}
                className={cn(
                  "w-full h-11 rounded-xl bg-muted/60 hover:bg-muted text-foreground/80 hover:text-foreground font-semibold text-[13px] transition-all active:scale-[0.98] group relative overflow-hidden px-4",
                  "border border-border/80 hover:border-primary/30 shadow-sm hover:shadow-md hover:-translate-y-0.5"
                )}
              >
                {/* Refined Shimmer Effect */}
                <div className="absolute inset-0 w-[200%] h-full bg-gradient-to-r from-transparent via-primary/[0.05] to-transparent -translate-x-[100%] group-hover:animate-[shimmer_2.5s_infinite] pointer-events-none" />
                
                <div className="flex items-center justify-between w-full relative z-10">
                  <div className="flex items-center gap-2.5">
                    <div className="relative">
                      <Plus className="h-4 w-4 text-primary/60 group-hover:text-primary group-hover:scale-110 transition-all duration-300" />
                    </div>
                    <span className="tracking-tight text-foreground/90 group-hover:text-foreground">New Plot Point</span>
                  </div>
                  <ChevronRight className="h-3.5 w-3.5 opacity-40 group-hover:translate-x-1 group-hover:opacity-100 transition-all duration-300" />
                </div>
              </Button>
            </DialogTrigger>
            <AddPlotCardDialogContent 
              onClose={() => setIsDialogOpen(false)}
              onSave={handleSavePlotCard}
              data={{
                chapterId: Number(chapterId),
                defaultChapterName: chapter?.title || 'this chapter',
                plotLines: plotLines
              }}
            />
          </Dialog>

          {/* New High-Resolution Edit Modal from Timeline */}
          <PlotCardDetailsModal 
            open={isEditModalOpen}
            onOpenChange={setIsEditModalOpen}
            card={selectedCard ? {
              ...selectedCard,
              // Add virtual properties for breadcrumbs
              chapter_name: chapter?.title || 'This Chapter',
              plotline_name: plotLines.find((pl: any) => Number(pl.id) === Number(selectedCard.x))?.title || 'Main Timeline'
            } as any : null}
            onSave={handleUpdatePlotCard}
            onDelete={handleDeletePlotCard}
            onDuplicate={handleDuplicatePlotCard}
          />
        </div>
      </div>
    </div>
  );
}

interface PlotCardStyle {
  dot: string;
  card: string;
  hover: string;
}

function PlotCardItem({ 
  card, 
  getStyle, 
  onClick 
}: { 
  card: PlotCard, 
  getStyle: (type: string) => PlotCardStyle,
  onClick: () => void
}) {
  const [isHovered, setIsHovered] = useState(false);
  const style = getStyle(card.type);

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onClick}
      className={`group relative p-4 rounded-xl border transition-all duration-200 ${style.card} hover:shadow-lg hover:border-primary/30 active:scale-[0.99] cursor-pointer`}
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${style.dot} ring-2 ring-background opacity-80`} />
          <h5 className="text-sm font-semibold text-foreground truncate">
            {card.title}
          </h5>
        </div>
        <Badge variant="outline" className="text-[10px] uppercase tracking-wider font-medium opacity-60 group-hover:opacity-100 bg-background/50 backdrop-blur-sm border-0">
          {card.type}
        </Badge>
      </div>

      <div
        className="overflow-hidden transition-[max-height,mask-image] duration-300 ease-in-out"
        style={{
          maxHeight: isHovered ? "1000px" : "4.5em",
          maskImage: isHovered
            ? "linear-gradient(to bottom, black 100%, black 100%)"
            : "linear-gradient(to bottom, black 60%, transparent 100%)",
          WebkitMaskImage: isHovered
            ? "linear-gradient(to bottom, black 100%, black 100%)"
            : "linear-gradient(to bottom, black 60%, transparent 100%)"
        }}
      >
        <p className="text-xs text-muted-foreground leading-relaxed">
          {card.content}
        </p>
      </div>
    </div>
  );
}
